import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { StyledModal, StyledModalBody } from '../../../../../components/Modals';
import { Fonts, Palette } from '../../../../../components/utils';
import { closeActiveModal } from '../../../../../helper/modals';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CheckWhiteIcon } from '../../../../../assets/icons/checkWhite.svg';

const MODAL_KEY = 'amenityCreateResult';

const Shell = styled.div`
  box-sizing: border-box;
  width: 460px;
  max-width: 100%;
  margin: 0 auto;
  padding: 24px;
  background: ${Palette.WHITE};
  box-shadow: 0px 1px 15px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const HeroWrap = styled.div`
  box-sizing: border-box;
  width: 403px;
  max-width: 100%;
  height: 189px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  background: linear-gradient(129deg, #133155 0%, rgba(41.8, 107.8, 187, 0) 100%);
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.24);
    pointer-events: none;
  }
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 16px;
    background: linear-gradient(
      180deg,
      rgba(19, 49, 85, 0) 72%,
      rgba(19, 49, 85, 0.08) 100%
    );
    pointer-events: none;
  }
`;

const GlassPanel = styled.div`
  width: 185px;
  height: 171px;
  left: 114.5px;
  top: 45px;
  position: absolute;
  overflow: hidden;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.28);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.22),
    inset 0 -10px 25px rgba(19, 49, 85, 0.08);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
`;

const HeroIconWrap = styled.div`
  width: 57px;
  height: 56px;
  left: 62px;
  top: 46px;
  position: absolute;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroCheckIcon = styled(CheckWhiteIcon)`
  width: 57px;
  height: 56px;
  display: block;
`;

const Title = styled.h2`
  margin: 0;
  color: ${Palette.BLACK};
  font-family: ${Fonts.RED_HAT_FONT}, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 36px;
  text-align: center;
`;

const Description = styled.p`
  margin: 0;
  box-sizing: border-box;
  width: 100%;
  max-width: 412px;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  color: ${({ $warning }) => ($warning ? Palette.ORANGE_500 : Palette.CONTENT_TERTIARY)};
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
`;

const TextBlock = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 412px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const DoneButton = styled.button`
  min-width: 142px;
  padding: 4px 16px;
  background: ${Palette.HAZY_BLUE};
  border: none;
  border-radius: 999px;
  color: ${Palette.WHITE};
  font-family: ${Fonts.OPEN_SANS_FONT}, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;

  &:hover {
    filter: brightness(1.05);
  }
`;

const AmenityCreateResultModalV2 = () => {
  const { t } = useTranslation();
  const { modal, data } = useSelector((state) => state.headerModal);
  const open = modal === MODAL_KEY;
  const variant = data?.variant ?? 'success';
  const title = data?.title ?? t('Amenity Created');
  const description = data?.description ?? '';

  return (
    <StyledModal
      show={open}
      closeOnBackdrop={false}
      onClose={closeActiveModal}
      width="460px"
      cssOverride={`
        .modal-dialog {
          width: 460px !important;
          max-width: 460px !important;
        }
        .modal-content {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          padding: 0 !important;
          gap: 0 !important;
          width: 460px !important;
          max-width: 460px !important;
        }
      `}>
      <StyledModalBody
        gap="0"
        cssOverride={`
          overflow-x: visible !important;
          overflow-y: visible !important;
          padding: 0 !important;
          padding-right: 0 !important;
        `}>
        <Shell>
          <HeroWrap>
            <GlassPanel>
              <HeroIconWrap>
                <HeroCheckIcon aria-hidden="true" focusable="false" />
              </HeroIconWrap>
            </GlassPanel>
          </HeroWrap>
          <TextBlock>
            <Title>{title}</Title>
            <Description $warning={variant === 'warning'}>{description}</Description>
          </TextBlock>
          <DoneButton type="button" onClick={closeActiveModal}>
            {t('Done')}
          </DoneButton>
        </Shell>
      </StyledModalBody>
    </StyledModal>
  );
};

export default AmenityCreateResultModalV2;
export { MODAL_KEY as AMENITY_CREATE_RESULT_MODAL };
